import { NgModule } from '@angular/core';
import { Routes, RouterModule, ExtraOptions, RouteReuseStrategy } from '@angular/router';

import {
  MissingPetResolverService,
  MissingPetEditResolverService,
  MissingPetSubmissionResolverService,
  MissingPetDraftReportResolverService,
  PetResolverService,
} from './services/resolvers/pets-resolver.service';
import { PetplanEligiblePetsResolverService } from './services/resolvers/petplan-eligible-pets-resolver.service';
import { AuthGuard } from './services/guards/auth.guard';
import { UserMeResolverService } from './services/resolvers/user-resolver.service';
import { RoutingComponents } from './pages/pages.module';
import { InvoicesResolverService } from './services/resolvers/invoices-resolver.service';
import { CustomRouteReuseStrategy } from './app.custom-route-reuse-strategy';
import { PerksResolverService } from './services/resolvers/perks-resolver.service';
import {
  ACCOUNT_TYPE_PREVIEW_ROUTE,
  ACTIVATE_NO_EMAIL_USER_ROUTE,
  CLAIM_ACCOUNT_ROUTE,
  EMAIL_ACTIVATE_ROUTE,
  EMAIL_SENT_ROUTE,
  IDENTIBASE_SUBSCRIBE_APPRECIATION_ROUTE,
  MEMBERSHIP_OPTIONS_ROUTE,
  OWNER_DETAILS_ROUTE,
  PURINA_ROUTE,
  QR_CODE_PREFERENCES_ROUTE,
  QR_CODE_ROUTE,
  TOK_CLAIM_ACCOUNT,
  TOK_DETAILS,
  TOK_LOGIN,
  TOK_PAYMENT,
} from './constants/route.constants';
import {
  QrCodeDataResolverService,
  QrCodePreferencesResolverService,
} from './services/resolvers/qr-code-resolver.service';
import { DashboardInitGuard } from './services/guards/dashboard-init.guard';

const routes: Routes = [
  {
    path: '',
    component: RoutingComponents.HomeComponent,
    canActivate: [AuthGuard],
    data: {
      meta: {
        title: 'Welcome to identibase',
      },
    },
  },
  {
    path: 'cvsstaff',
    redirectTo: '',
    pathMatch: 'full',
  },
  {
    path: 'dashboard',
    canActivate: [DashboardInitGuard],
    resolve: {
      user: UserMeResolverService,
    },
    children: [
      {
        path: '',
        component: RoutingComponents.DashboardComponent,
        resolve: {
          perks: PerksResolverService,
        },
        data: {
          meta: {
            title: 'Dashboard',
          },
        },
      },
    ],
  },
  {
    path: 'identibase-plus',
    component: RoutingComponents.IdentibasePlusPageComponent,
    data: {
      meta: {
        title: 'identibase+',
        description:
          'Activate your pet’s Digital ID to stay connected and contactable at all times',
      },
    },
  },
  {
    path: 'identialert',
    component: RoutingComponents.IdentialertPageComponent,
    data: {
      meta: {
        title: 'identialert',
      },
    },
  },
  {
    path: 'identifind',
    component: RoutingComponents.IdentifindPageComponent,
    data: {
      meta: {
        title: 'identifind',
      },
    },
  },
  {
    path: 'pit-pat',
    component: RoutingComponents.PitPatPageComponent,
    data: {
      meta: {
        title: 'PitPat',
      },
    },
  },
  {
    path: 'acceptable-use',
    component: RoutingComponents.AcceptableUseComponent,
    data: {
      meta: {
        title: 'Website acceptable use policy',
      },
    },
  },
  {
    path: 'account',
    runGuardsAndResolvers: 'always',
    resolve: {
      user: UserMeResolverService,
    },
    children: [
      {
        path: '',
        component: RoutingComponents.AccountComponent,
        data: {
          meta: {
            title: 'My account',
          },
        },
      },
      {
        path: 'address',
        children: [
          {
            path: 'new',
            component: RoutingComponents.AddAddressComponent,
            data: {
              meta: {
                title: 'Add your address',
              },
            },
          },
          {
            path: ':id/edit',
            component: RoutingComponents.UpdateAddressComponent,
            data: {
              reloadComponent: true,
              meta: {
                title: 'Update your address',
              },
            },
          },
        ],
      },
      {
        path: 'update-email',
        component: RoutingComponents.UpdateEmailComponent,
        data: {
          meta: {
            title: 'Change account email',
          },
        },
      },
      {
        path: 'update-password',
        component: RoutingComponents.UpdatePasswordComponent,
        data: {
          meta: {
            title: 'Change password',
          },
        },
      },
      {
        path: 'update-telephone',
        component: RoutingComponents.UpdateTelephoneComponent,
        data: {
          meta: {
            title: 'Phone number',
          },
        },
      },
      {
        path: 'update-gender',
        component: RoutingComponents.UpdateGenderComponent,
        data: {
          meta: {
            title: 'Gender',
          },
        },
      },
      {
        path: 'update-title',
        component: RoutingComponents.UpdateTitleComponent,
        data: {
          meta: {
            title: 'Title',
          },
        },
      },
      {
        path: 'update-date-of-birth',
        component: RoutingComponents.UpdateDateOfBirthComponent,
        data: {
          meta: {
            title: 'Date of birth',
          },
        },
      },
      {
        path: 'account-type',
        component: RoutingComponents.UpgradeAccountComponent,
        data: {
          meta: {
            title: 'Membership type',
          },
        },
      },
      {
        path: 'invoices',
        component: RoutingComponents.InvoicesComponent,
        resolve: {
          invoices: InvoicesResolverService,
        },
        data: {
          meta: {
            title: 'Invoices',
          },
        },
      },
      {
        path: 'user-details',
        component: RoutingComponents.RegisterUserDetailsConfirmationComponent,
        data: {
          isUpdateDetails: true,
          meta: {
            title: 'Your details',
          },
        },
      },
      {
        path: OWNER_DETAILS_ROUTE,
        component: RoutingComponents.OwnerDetailsComponent,
        data: {
          meta: {
            title: 'Owner details',
          },
        },
      },
      {
        path: MEMBERSHIP_OPTIONS_ROUTE,
        component: RoutingComponents.MembershipOptionsComponent,
        data: {
          meta: {
            title: 'Membership options',
          },
        },
      },
      {
        path: IDENTIBASE_SUBSCRIBE_APPRECIATION_ROUTE,
        component: RoutingComponents.IdentibaseSubAppreciationComponent,
        data: {
          meta: {
            title: 'identibase subscription route',
          },
        },
      },
    ],
  },
  {
    path: 'login',
    component: RoutingComponents.LoginComponent,
    data: {
      meta: {
        title: 'Log in',
      },
    },
  },
  {
    path: 'login-register',
    component: RoutingComponents.LoginRegisterComponent,
    data: {
      meta: {
        title: 'My account',
      },
    },
  },
  {
    path: 'forgot-password',
    component: RoutingComponents.ForgotPasswordComponent,
    data: {
      meta: {
        title: 'Forgot password',
      },
    },
  },
  {
    path: 'set-password/:token',
    component: RoutingComponents.SetNewPasswordComponent,
    data: {
      meta: {
        title: 'Set your new password',
      },
    },
  },
  {
    path: 'social-login-register',
    component: RoutingComponents.SocialLoginRegisterComponent,
    data: {
      meta: {
        title: 'Social login register',
      },
    },
  },
  {
    path: 'register',
    children: [
      {
        path: '',
        component: RoutingComponents.RegisterComponent,
        data: {
          meta: {
            title: 'Register',
            description: 'Sign up and easily register your pet’s microchip details today',
          },
        },
      },
      {
        path: 'user-details',
        component: RoutingComponents.RegisterUserDetailsConfirmationComponent,
        resolve: {
          user: UserMeResolverService,
        },
        data: {
          meta: {
            title: 'Your details',
          },
        },
      },
    ],
  },
  {
    path: 'choose-plan',
    component: RoutingComponents.ChoosePlanComponent,
    data: {
      meta: {
        title: 'Choose a plan',
      },
    },
  },
  {
    path: 'contact-us',
    component: RoutingComponents.ContactUsComponent,
    data: {
      meta: {
        title: 'Contact us',
        description: 'Need help with your account or lost your pet? We’re happy to help',
      },
    },
  },
  {
    path: 'identi-professionals',
    component: RoutingComponents.IdentiProfessionalsComponent,
    data: {
      meta: {
        title: 'identi professionals',
        description: 'Connecting breeders with unmatched support and exclusive benefits.',
      },
    },
  },
  {
    path: 'identibase-review',
    component: RoutingComponents.IdentibaseReviewComponent,
    data: {
      meta: {
        title: 'Identibase 2024 year in review',
      },
    },
  },
  {
    path: 'tok-consent',
    component: RoutingComponents.TokConsentComponent,
    data: {
      meta: {
        title: 'Tok Consent',
      },
    },
  },

  {
    path: 'faq',
    redirectTo: 'contact-us',
    pathMatch: 'full',
  },
  {
    path: 'cookies',
    component: RoutingComponents.CookiesComponent,
    data: {
      meta: {
        title: 'Cookie policy',
      },
    },
  },
  {
    path: 'lost-and-found',
    component: RoutingComponents.LostAndFoundComponent,
    data: {
      meta: {
        title: 'Lost and found',
      },
    },
  },
  {
    path: 'lost-and-found/missing-pet-report/:reference',
    component: RoutingComponents.MissingPetReportComponent,
    resolve: {
      missingPetReport: MissingPetResolverService,
    },
    data: {
      meta: {
        title: 'Lost and found missing pet report',
      },
    },
  },
  {
    path: 'lost-and-found/missing-pet/:reference',
    runGuardsAndResolvers: 'always',
    resolve: {
      user: UserMeResolverService,
    },
    data: {
      reloadComponent: true,
      skipUserMeAuth: true,
    },
    children: [
      {
        path: '',
        component: RoutingComponents.MissingPetComponent,
        resolve: {
          missingPetReport: MissingPetResolverService,
        },
      },
      {
        path: 'edit',
        component: RoutingComponents.ReportMissingPetComponent,
        resolve: {
          pet: MissingPetEditResolverService,
        },
        data: {
          meta: {
            title: 'Edit your pet missing report',
          },
        },
      },
    ],
  },
  {
    path: 'rehome-pet',
    runGuardsAndResolvers: 'always',
    resolve: {
      user: UserMeResolverService,
    },
    data: {
      reloadComponent: true,
    },
    children: [
      {
        path: '',
        component: RoutingComponents.RehomePetComponent,
      },
    ],
  },
  { path: 'catsprotectioncheckyourchip', redirectTo: 'catsprotection', pathMatch: 'full' },
  {
    path: 'catsprotection',
    component: RoutingComponents.CheckPetsChipComponent,
    data: {
      meta: {
        title: 'Check your pets microchip',
      },
    },
  },
  {
    path: PURINA_ROUTE,
    component: RoutingComponents.PurinaCheckChipComponent,
    data: {
      meta: {
        title: 'Purina check microchip',
      },
    },
  },
  {
    path: 'microchipping-your-pet',
    component: RoutingComponents.MicrochippingYourPetComponent,
    data: {
      meta: {
        title: 'Microchipping your pet',
        description: 'Everything you need to know about microchipping',
      },
    },
  },
  {
    path: 'my-pets',
    component: RoutingComponents.MyPetsComponent,
    resolve: {
      user: UserMeResolverService,
    },
    data: {
      meta: {
        title: 'My pets',
      },
    },
  },
  {
    path: 'my-pet/:id',
    runGuardsAndResolvers: 'always',
    resolve: {
      user: UserMeResolverService,
    },
    data: {
      reloadComponent: true,
    },
    children: [
      {
        path: '',
        component: RoutingComponents.MyPetComponent,
        resolve: {
          pet: PetResolverService,
        },
        data: {
          meta: {
            title: 'Pet details',
          },
        },
      },
      {
        path: 'edit',
        component: RoutingComponents.EditMyPetComponent,
        resolve: {
          pet: PetResolverService,
        },
        data: {
          meta: {
            title: 'Edit pet details',
          },
        },
      },
      {
        path: 'confirm',
        component: RoutingComponents.EditMyPetComponent,
        resolve: {
          pet: PetResolverService,
        },
        data: {
          meta: {
            title: "Your pet's details",
          },
        },
      },
      {
        path: 'report-missing',
        component: RoutingComponents.ReportMissingPetComponent,
        resolve: {
          pet: MissingPetSubmissionResolverService,
          reports: MissingPetDraftReportResolverService,
        },
        data: {
          meta: {
            title: 'Report a pet missing',
          },
        },
      },
    ],
  },
  {
    path: 'claim-pet',
    resolve: {
      user: UserMeResolverService,
    },
    data: {
      checkDetailsProvided: true,
    },
    children: [
      {
        path: '',
        component: RoutingComponents.ClaimPetByChipComponent,
        data: {
          meta: {
            title: "Your pet's details",
          },
        },
      },
    ],
  },
  {
    path: 'privacy',
    component: RoutingComponents.PrivacyComponent,
    data: {
      meta: {
        title: 'Privacy policy',
      },
    },
  },
  {
    path: 'social',
    component: RoutingComponents.SocialLandingPageComponent,
    data: {
      meta: {
        title: 'Welcome to identibase',
        description:
          'identibase is a national database containing the details of microchipped pets and their keepers',
      },
    },
  },
  {
    path: 'perk-terms',
    resolve: {
      user: UserMeResolverService,
    },
    children: [
      {
        path: '',
        component: RoutingComponents.PerkTermsComponent,
        resolve: {
          perks: PerksResolverService,
        },
        data: {
          meta: {
            title: 'Perks and offers terms and conditions',
          },
        },
      },
    ],
  },
  {
    path: 'welcome-gift',
    component: RoutingComponents.PetplanWelcomePackComponent,
    resolve: {
      user: UserMeResolverService,
      eligiblePets: PetplanEligiblePetsResolverService,
    },
    data: {
      meta: {
        title: 'Free Petplan Insurance',
      },
    },
  },
  {
    path: 'petplan-application',
    resolve: {
      user: UserMeResolverService,
      eligiblePets: PetplanEligiblePetsResolverService,
    },
    data: {
      meta: {
        title: 'Free Veterinary Cover From Petplan',
      },
    },
    children: [
      {
        path: 'welcome-gift',
        component: RoutingComponents.PetplanWelcomePackComponent,
      },
    ],
  },
  {
    path: 'terms-and-conditions',
    component: RoutingComponents.TermsAndConditionsComponent,
    data: {
      meta: {
        title: 'Our terms and conditions',
      },
    },
  },
  // TODO: commented out because this route only need in development
  // {
  //   path: 'theme-kit',
  //   component: RoutingComponents.BootstrapThemeKitComponent,
  // },
  {
    path: 'website-use',
    component: RoutingComponents.WebsiteUseComponent,
    data: {
      meta: {
        title: 'Terms of website use',
      },
    },
  },
  {
    path: ACCOUNT_TYPE_PREVIEW_ROUTE,
    component: RoutingComponents.UpgradeAccountComponent,
    data: {
      skipUserMeAuth: true,
      meta: {
        title: 'Membership type',
      },
    },
  },
  {
    path: EMAIL_ACTIVATE_ROUTE,
    component: RoutingComponents.ActivateEmailComponent,
  },
  {
    path: EMAIL_SENT_ROUTE,
    component: RoutingComponents.SentEmailComponent,
  },
  {
    path: CLAIM_ACCOUNT_ROUTE,
    component: RoutingComponents.ClaimAccountComponent,
    data: {
      meta: {
        title: 'Claim account',
      },
    },
  },
  {
    path: TOK_CLAIM_ACCOUNT,
    component: RoutingComponents.TokClaimAccountComponent,
    data: {
      meta: {
        title: 'Transfer of Keepership Claim Account',
      },
    },
  },
  {
    path: TOK_LOGIN,
    component: RoutingComponents.TokLoginComponent,
    data: {
      meta: {
        title: 'Transfer of Keepership Login',
      },
    },
  },
  {
    path: ACTIVATE_NO_EMAIL_USER_ROUTE,
    component: RoutingComponents.ActivateNoEmailUserComponent,
    data: {
      meta: {
        title: 'Activate no email user',
      },
    },
  },
  {
    path: `${QR_CODE_ROUTE}/:id`,
    component: RoutingComponents.QrCodePageComponent,
    resolve: {
      qrCodeData: QrCodeDataResolverService,
    },
    data: {
      meta: {
        title: 'QR-Code Data',
      },
    },
  },
  {
    path: 'smart-tag-for-pet/:id',
    runGuardsAndResolvers: 'always',
    resolve: {
      user: UserMeResolverService,
    },
    data: {
      reloadComponent: true,
    },
    children: [
      {
        path: QR_CODE_PREFERENCES_ROUTE,
        component: RoutingComponents.QrCodePreferencesPageComponent,
        resolve: {
          qrCodePreferences: QrCodePreferencesResolverService,
          pet: PetResolverService,
        },
        data: {
          meta: {
            title: 'QR code preferences',
          },
        },
      },
    ],
  },
  {
    path: TOK_PAYMENT,
    component: RoutingComponents.TokPaymentComponent,
    data: {
      meta: {
        title: 'tok-payment',
      },
    },
  },
  {
    path: TOK_DETAILS,
    runGuardsAndResolvers: 'always',
    resolve: {
      user: UserMeResolverService,
    },
    component: RoutingComponents.TokDetailsComponent,
    data: {
      meta: {
        title: 'tok-payment',
      },
    },
  },
  {
    path: 'pet-deceased',
    component: RoutingComponents.PetDeceasedComponent,
    data: {
      meta: {
        title: 'pet-deceased',
      },
    },
  },
  {
    path: '404',
    component: RoutingComponents.NotFoundComponent,
    data: {
      meta: {
        title: 'Page not found',
        prerenderStatusCode: 404,
      },
    },
  },
  {
    path: '**',
    redirectTo: '/404',
  },
];

const routingOptions: ExtraOptions = {
    scrollOffset: [0, 0],
    scrollPositionRestoration: 'top',
    anchorScrolling: 'enabled',
};

@NgModule({
  declarations: [],
  imports: [RouterModule.forRoot(routes, routingOptions)],
  providers: [
    {
      provide: RouteReuseStrategy,
      useClass: CustomRouteReuseStrategy,
    },
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
